import React from 'react';
import { CircularProgress } from '@mui/material';
import { Check } from '@mui/icons-material';
import OrderIcon from '../../assets/svgs/OrderIcon';
import ProductIcon from '../../assets/svgs/ProductIcon';
import UsersIcon from '../../assets/svgs/UsersIcon';
import { Assessment, Email, Receipt, Store } from '@mui/icons-material';
import { getCurrency } from '../../services/tableHelper';

interface SubscriptionPlan {
	id: number;
	name: string;
	display_name: string;
	billing_interval: 'monthly' | 'annually';
	price: number;
	currency: string;
	features: {
		analytics: boolean;
		emailSupport: boolean;
		invoiceReadReceipts: boolean;
		onlineStore: boolean;
	};
	sales_limit: number | null;
	products_limit: number | null;
	team_members_limit: number | null;
	is_active: boolean;
	created_at: string;
	updated_at: string;
}

interface PlansResponse {
	monthly: SubscriptionPlan[];
	annually: SubscriptionPlan[];
}

interface CurrentSubscription {
	id: number;
	shop_id: number;
	plan_id: number;
	status: 'active' | 'past_due' | 'canceled' | 'pending_downgrade' | 'expired';
	current_period_start: string;
	current_period_end: string;
	next_billing_date: string | null;
	pending_plan_id: number | null;
	paystack_customer_code: string | null;
	plan_name: string;
	display_name: string;
	price: number;
	features: any;
	is_cancelling?: boolean;
}

interface PlansProps {
	plansData: PlansResponse;
	currentSubscription: CurrentSubscription | null;
	selectedPeriod: 'monthly' | 'annually';
	onPeriodChange: (period: 'monthly' | 'annually') => void;
	onPlanSelect: (plan: SubscriptionPlan) => void;
	processing: boolean;
	currency: string;
}

const Plans: React.FC<PlansProps> = ({
	plansData,
	currentSubscription,
	selectedPeriod,
	onPeriodChange,
	onPlanSelect,
	processing,
	currency,
}) => {
	// Helper function to get plan features
	const getPlanFeatures = (plan: SubscriptionPlan) => {
		const features = [];

		// Sales limit
		if (plan.sales_limit) {
			features.push({
				icon: (
					<div style={{ fill: '#10B981' }}>
						<OrderIcon width={20} height={20} />
					</div>
				),
				text: `Up to ${plan.sales_limit} sales per month`,
			});
		} else {
			features.push({
				icon: (
					<div style={{ fill: '#10B981' }}>
						<OrderIcon width={20} height={20} />
					</div>
				),
				text: 'Unlimited sales',
			});
		}

		// Products limit
		if (plan.products_limit) {
			features.push({
				icon: (
					<div style={{ fill: '#10B981' }}>
						<ProductIcon width={20} height={20} />
					</div>
				),
				text: `Up to ${plan.products_limit} products`,
			});
		} else {
			features.push({
				icon: (
					<div style={{ fill: '#10B981' }}>
						<ProductIcon width={20} height={20} />
					</div>
				),
				text: 'Unlimited products',
			});
		}

		// Team members limit
		if (plan.team_members_limit) {
			features.push({
				icon: (
					<div style={{ fill: '#10B981' }}>
						<UsersIcon width={20} height={20} />
					</div>
				),
				text: `Up to ${plan.team_members_limit} team members`,
			});
		} else {
			features.push({
				icon: (
					<div style={{ fill: '#10B981' }}>
						<UsersIcon width={20} height={20} />
					</div>
				),
				text: 'Unlimited team members',
			});
		}

		// Feature flags - check if features exist and has the properties
		if (plan.features && plan.features.analytics) {
			features.push({
				icon: <Assessment style={{ color: '#10B981', fontSize: 20 }} />,
				text: 'Analytics & Reports',
			});
		}

		if (plan.features && plan.features.emailSupport) {
			features.push({
				icon: <Email style={{ color: '#10B981', fontSize: 20 }} />,
				text: 'Email Support',
			});
		}

		if (plan.features && plan.features.invoiceReadReceipts) {
			features.push({
				icon: <Receipt style={{ color: '#10B981', fontSize: 20 }} />,
				text: 'Invoice Read Receipts',
			});
		}

		if (plan.features && plan.features.onlineStore) {
			features.push({
				icon: <Store style={{ color: '#10B981', fontSize: 20 }} />,
				text: 'Online Store',
			});
		}

		return features;
	};

	// Helper function to check if a plan is the current plan
	const isCurrentPlan = (plan: SubscriptionPlan) => {
		if (!currentSubscription || !currentSubscription.plan_name) return false;

		// Don't show as current plan if subscription is expired or canceled
		const now = new Date();
		if (
			currentSubscription.status === 'expired' ||
			(currentSubscription.status === 'canceled' && new Date(currentSubscription.current_period_end) < now) ||
			new Date(currentSubscription.current_period_end) < now
		) {
			return false;
		}

		return currentSubscription.plan_name === plan.name;
	};

	// Helper function to get button text based on subscription status
	const getButtonText = (targetPlan: SubscriptionPlan, isCurrent: boolean) => {
		if (processing) return <CircularProgress size={20} />;
		if (isCurrent) return 'Current Plan';

		// If no active subscription, just show "Choose Plan"
		if (!currentSubscription || !['active', 'pending_downgrade'].includes(currentSubscription.status)) {
			return 'Choose Plan';
		}

		// User has active subscription, show "Switch to This Plan"
		return 'Switch to This Plan';
	};

	return (
		<div>
			{/* Billing Period Toggle */}
			<div className="flex justify-end mb-6">
				<div className="inline-flex rounded-lg p-1 bg-gray-100">
					<button
						onClick={() => onPeriodChange('monthly')}
						className={`px-6 py-2 rounded-md font-medium transition-all ${
							selectedPeriod === 'monthly'
								? 'bg-blue-600 text-white'
								: 'text-gray-700 hover:text-gray-900'
						}`}
					>
						Monthly
					</button>
					<button
						onClick={() => onPeriodChange('annually')}
						className={`px-6 py-2 rounded-md font-medium transition-all ${
							selectedPeriod === 'annually'
								? 'bg-blue-600 text-white'
								: 'text-gray-700 hover:text-gray-900'
						}`}
					>
						Yearly
					</button>
				</div>
			</div>

			{/* Plans Grid or Empty State */}
			{!plansData[selectedPeriod] || plansData[selectedPeriod].length === 0 ? (
				<div className="text-center py-16">
					<h3 className="text-xl text-gray-400 mb-2">No {selectedPeriod} subscription plans available</h3>
					<p className="text-gray-500">
						Please contact support if you need assistance with subscription plans.
					</p>
				</div>
			) : (
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-6">
					{plansData[selectedPeriod].map((plan, index) => {
						const isGrowthPlan = plan.name.toLowerCase().includes('growth') || index === 1;
						const isCurrent = isCurrentPlan(plan);

						return (
							<div
								key={`${plan.name}_${plan.billing_interval}`}
								className={`rounded-lg p-8 relative transition-all bg-white text-gray-900 border border-gray-200 ${
									isGrowthPlan ? 'shadow-xl transform scale-105' : ''
								}`}
							>
								{/* Plan Type Badge */}
								<div className="flex justify-between items-center mb-4">
									{/* Plan Name */}
									<h3 className="text-xl font-bold text-gray-900">{plan.display_name}</h3>

									{/* Plan Type Badge */}
									{plan.name.toLowerCase().includes('growth') && (
										<span
											className={`px-3 py-1 rounded-full text-xs font-semibold text-white bg-orange-600`}
										>
											POPULAR
										</span>
									)}
								</div>

								{/* Price */}
								<div className="mb-8">
									<span className="text-3xl font-bold text-gray-900">
										{getCurrency(currency as any)}
										{typeof plan.price === 'string'
											? (parseFloat(plan.price) / 100).toFixed(0)
											: (plan.price / 100).toFixed(0)}
									</span>
									<span className="text-md text-gray-600">
										/{selectedPeriod === 'monthly' ? 'month' : 'year'}
									</span>
								</div>

								{/* Action Button */}
								<div className="mb-8">
									<button
										onClick={() => onPlanSelect(plan)}
										disabled={isCurrent || processing}
										className={`w-full py-3 px-6 rounded-lg font-medium transition-all ${
											isCurrent
												? 'bg-gray-300 text-gray-500 cursor-not-allowed'
												: 'bg-gray-800 text-white hover:bg-gray-900'
										} disabled:opacity-50 disabled:cursor-not-allowed`}
									>
										{getButtonText(plan, isCurrent)}
									</button>
								</div>

								{/* Features List */}
								<ul className="space-y-2">
									{getPlanFeatures(plan).map((feature, idx) => (
										<li key={idx} className="flex items-start">
											<Check className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-green-500" />
											<span className="text-sm text-gray-700">{feature.text}</span>
										</li>
									))}
								</ul>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default Plans;
